import React, { useState, useEffect, useCallback } from 'react';
import { useRealtimeRefresh } from '../../hooks/useRealtimeRefresh';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Truck, Plus, Pencil, Trash2, Calendar, CheckCircle, Clock, X, 
  Package, AlertTriangle, PackageCheck, FileText, Eye, ShoppingCart,
  ChevronDown, ChevronRight, Layers, ArrowUpDown, ArrowUp, ArrowDown,
  Upload, MessageSquare, FileCheck, History, Info, Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DepotIncoming = ({ branchIdOverride, readOnly = false }) => {
  const { user } = useAuth();
  const [urunler, setUrunler] = useState([]);
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('bekliyor');
  
  // Bilgilendirme kutusu state'i (localStorage'dan oku)
  const [showGelecekUrunlerInfo, setShowGelecekUrunlerInfo] = useState(() => {
    return localStorage.getItem('depot_gelecek_urunler_info_hidden') !== 'true';
  });
  
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    id: '',
    cari_hesap_id: '',
    cari_hesap_name: '',
    product_name: '',
    quantity: '',
    unit: 'adet',
    expected_date: '',
    notes: '',
    status: 'bekliyor'
  });

  // Teslim Al modal state
  const [teslimAlModal, setTeslimAlModal] = useState(false);
  const [selectedUrun, setSelectedUrun] = useState(null);
  const [teslimItems, setTeslimItems] = useState([]);
  const [teslimNotu, setTeslimNotu] = useState('');
  const [teslimSubmitting, setTeslimSubmitting] = useState(false);
  const [irsaliyeMiktar, setIrsaliyeMiktar] = useState('');
  const [teslimBelgeler, setTeslimBelgeler] = useState([]);

  // Detail modal state
  const [detailModal, setDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);
  
  // Delivery history modal state
  const [historyModal, setHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState({});
  
  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: 'order_date', direction: 'desc' });

  // Check if item is unread by current user
  const isUnread = useCallback((urun) => {
    if (!user?.id) return false;
    const readBy = urun.read_by || [];
    return !readBy.includes(user.id);
  }, [user?.id]);

  // Mark single item as read
  const markAsRead = useCallback(async (urunId) => {
    try {
      await axios.post(
        `${API}/api/depot/gelecek-urunler/${urunId}/mark-read`,
        {},
        { headers: getAuthHeaders() }
      );
      // Update local state
      setUrunler(prev => prev.map(u => {
        if (u.id === urunId) {
          return { ...u, read_by: [...(u.read_by || []), user?.id] };
        }
        return u;
      }));
    } catch (err) {
      console.error('Okundu işaretleme hatası:', err);
    }
  }, [user?.id]);

  // Mark multiple items as read
  const markMultipleAsRead = useCallback(async (urunIds) => {
    if (!urunIds || urunIds.length === 0) return;
    try {
      await axios.post(
        `${API}/api/depot/gelecek-urunler/mark-all-read`,
        { urun_ids: urunIds },
        { headers: getAuthHeaders() }
      );
      // Update local state
      setUrunler(prev => prev.map(u => {
        if (urunIds.includes(u.id)) {
          return { ...u, read_by: [...(u.read_by || []), user?.id] };
        }
        return u;
      }));
    } catch (err) {
      console.error('Toplu okundu işaretleme hatası:', err);
    }
  }, [user?.id]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json'
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // branchIdOverride varsa query parametresi olarak ekle
      const branchParam = branchIdOverride ? `?branch_id=${branchIdOverride}` : '';
      const [urunlerRes, cariRes] = await Promise.all([
        axios.get(`${API}/api/depot/gelecek-urunler${branchParam}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/depot/cari-hesaplar`, { headers: getAuthHeaders() })
      ]);
      setUrunler(urunlerRes.data);
      setCariHesaplar(cariRes.data);
    } catch (err) {
      console.error('Veri yüklenemedi:', err);
      setError('Veriler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [branchIdOverride]);

  useEffect(() => {
    fetchData();
    
    // Sayfayı görüntülendi olarak işaretle
    const markSeen = async () => {
      try {
        await axios.post(`${API}/api/depot/mark-seen/depot_incoming`, {}, {
          headers: getAuthHeaders()
        });
      } catch (err) {
        console.log('Mark seen error:', err);
      }
    };
    markSeen();
  }, [fetchData]);

  // Gerçek zamanlı: veri değişince otomatik yenile
  useRealtimeRefresh(() => fetchData());

  const resetForm = () => {
    setForm({
      id: '',
      cari_hesap_id: '',
      cari_hesap_name: '',
      product_name: '',
      quantity: '',
      unit: 'adet',
      expected_date: '',
      notes: '',
      status: 'bekliyor'
    });
    setEditMode(false);
  };

  // Fetch delivery history for an order
  const fetchDeliveryHistory = async (orderId) => {
    try {
      setHistoryLoading(true);
      const response = await axios.get(
        `${API}/api/depot/gelecek-urunler/${orderId}/delivery-history`,
        { headers: getAuthHeaders() }
      );
      setHistoryData(response.data);
      setHistoryModal(true);
    } catch (err) {
      console.error('Teslimat geçmişi yüklenemedi:', err);
      toast.error('Teslimat geçmişi yüklenirken bir hata oluştu');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openCreate = () => {
    resetForm();
    setModal(true);
  };

  const openEdit = (urun) => {
    setForm({
      id: urun.id,
      cari_hesap_id: urun.cari_hesap_id || '',
      cari_hesap_name: urun.cari_hesap_name || '',
      product_name: urun.product_name || '',
      quantity: urun.quantity || '',
      unit: urun.unit || 'adet',
      expected_date: urun.expected_date || '',
      notes: urun.notes || '',
      status: urun.status || 'bekliyor'
    });
    setEditMode(true);
    setModal(true);
  };

  const openTeslimAlModal = (urun) => {
    setSelectedUrun(urun);
    
    // Initialize teslim items from the urun's items (for purchasing-sourced) or single item
    if (urun.items && urun.items.length > 0) {
      setTeslimItems(urun.items.map((item, idx) => ({
        item_index: idx,
        stok_karti_id: item.stok_karti_id,
        product_name: item.product_name,
        expected_quantity: item.quantity || item.expected_quantity,
        received_quantity: 0, // Default to 0 - user must enter received amount
        irsaliye_quantity: '',
        unit: item.unit || 'adet',
        aciklama: ''
      })));
    } else {
      // Single item from manual entry
      setTeslimItems([{
        item_index: 0,
        stok_karti_id: urun.stok_karti_id || '',
        product_name: urun.product_name,
        expected_quantity: urun.quantity,
        received_quantity: 0, // Default to 0
        irsaliye_quantity: '',
        unit: urun.unit || 'adet',
        aciklama: ''
      }]);
    }
    
    setTeslimNotu('');
    setIrsaliyeMiktar('');
    setTeslimBelgeler([]);
    setTeslimAlModal(true);
  };

  const openDetailModal = (urun) => {
    setDetailData(urun);
    setDetailModal(true);
  };

  const handleTeslimQuantityChange = (index, value) => {
    setTeslimItems(prev => prev.map((item, idx) => 
      idx === index ? { ...item, received_quantity: parseFloat(value) || 0 } : item
    ));
  };

  const handleIrsaliyeQuantityChange = (index, value) => {
    setTeslimItems(prev => prev.map((item, idx) => 
      idx === index ? { ...item, irsaliye_quantity: value } : item
    ));
  };

  const handleAciklamaChange = (index, value) => {
    setTeslimItems(prev => prev.map((item, idx) => 
      idx === index ? { ...item, aciklama: value } : item
    ));
  };

  const handleBelgeUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setTeslimBelgeler(prev => [...prev, {
          name: file.name,
          type: file.type,
          data: event.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeBelge = (index) => {
    setTeslimBelgeler(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleTeslimAl = async () => {
    if (!selectedUrun) return;
    
    setTeslimSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/api/depot/gelecek-urunler/${selectedUrun.id}/teslim-al`,
        {
          items: teslimItems.map(item => ({
            item_index: item.item_index,
            received_quantity: item.received_quantity,
            irsaliye_quantity: item.irsaliye_quantity || null,
            aciklama: item.aciklama || ''
          })),
          teslim_notu: teslimNotu,
          belgeler: teslimBelgeler
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.has_shortage) {
        toast.warning(`Ürünler teslim alındı. ${response.data.shortage_count} üründe eksiklik kaydedildi.`);
      } else {
        toast.success('Ürünler başarıyla teslim alındı ve stoğa eklendi');
      }
      
      setTeslimAlModal(false);
      setSelectedUrun(null);
      setTeslimBelgeler([]);
      fetchData();
    } catch (err) {
      console.error('Teslim alma hatası:', err);
      toast.error(err.response?.data?.detail || 'Teslim alma işlemi başarısız');
    } finally {
      setTeslimSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.product_name) {
      toast.error('Ürün adı zorunludur');
      return;
    }

    try {
      if (editMode) {
        await axios.put(`${API}/api/depot/gelecek-urunler/${form.id}`, form, { headers: getAuthHeaders() });
        toast.success('Kayıt güncellendi');
      } else {
        await axios.post(`${API}/api/depot/gelecek-urunler`, form, { headers: getAuthHeaders() });
        toast.success('Kayıt oluşturuldu');
      }
      setModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/depot/gelecek-urunler/${id}`, { headers: getAuthHeaders() });
      toast.success('Kayıt silindi');
      fetchData();
    } catch (err) {
      toast.error('Silme başarısız');
    }
  };

  const handleCariSelect = (e) => {
    const selectedId = e.target.value;
    const selected = cariHesaplar.find(c => c.id === selectedId);
    setForm(prev => ({
      ...prev,
      cari_hesap_id: selectedId,
      cari_hesap_name: selected ? selected.name : ''
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'teslim_alindi':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="h-3 w-3 mr-1" />Teslim Alındı</Badge>;
      case 'eksik_teslim':
        return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="h-3 w-3 mr-1" />Eksik Teslim</Badge>;
      case 'iptal':
        return <Badge variant="destructive"><X className="h-3 w-3 mr-1" />İptal</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="h-3 w-3 mr-1" />Bekliyor</Badge>;
    }
  };

  const getSourceBadge = (source) => {
    if (source === 'purchasing') {
      return <Badge className="bg-orange-100 text-orange-700"><ShoppingCart className="h-3 w-3 mr-1" />Satın Alma</Badge>;
    }
    return <Badge variant="outline"><Package className="h-3 w-3 mr-1" />Manuel</Badge>;
  };

  const birimOptions = ['adet', 'kg', 'metre', 'litre', 'paket', 'kutu', 'ton', 'metrekare', 'metre tül', 'metre küp'];

  // Belge indirme fonksiyonu
  const downloadBelge = (belge) => {
    const link = document.createElement('a');
    link.href = belge.data;
    link.download = belge.name || 'belge';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`${belge.name} indiriliyor...`);
  };

  // Toggle row expansion
  const toggleRowExpansion = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Sort handling
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'order_date':
          aValue = new Date(a.order_date || a.created_at || 0).getTime();
          bValue = new Date(b.order_date || b.created_at || 0).getTime();
          break;
        case 'expected_date':
          aValue = new Date(a.expected_date || a.estimated_arrival_datetime || 0).getTime();
          bValue = new Date(b.expected_date || b.estimated_arrival_datetime || 0).getTime();
          break;
        case 'cari_hesap_name':
          aValue = (a.cari_hesap_name || '').toLowerCase();
          bValue = (b.cari_hesap_name || '').toLowerCase();
          break;
        case 'item_count':
          aValue = a.items?.length || 1;
          bValue = b.items?.length || 1;
          break;
        case 'source':
          aValue = a.source || '';
          bValue = b.source || '';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 text-blue-600" />
      : <ArrowDown className="h-3 w-3 ml-1 text-blue-600" />;
  };

  // Filter by status
  const bekleyenUrunler = urunler.filter(u => u.status === 'bekliyor');
  const eksikKalanlar = urunler.filter(u => u.status === 'eksik_teslim');
  const tamamlananlar = urunler.filter(u => u.status === 'teslim_alindi');
  
  // Count unread items per category
  const unreadBekleyen = bekleyenUrunler.filter(u => isUnread(u)).length;
  const unreadEksik = eksikKalanlar.filter(u => isUnread(u)).length;
  const unreadTamamlanan = tamamlananlar.filter(u => isUnread(u)).length;
  
  const getFilteredUrunler = () => {
    switch (activeTab) {
      case 'bekliyor':
        return sortData(bekleyenUrunler);
      case 'eksik':
        return sortData(eksikKalanlar);
      case 'tamamlanan':
        return sortData(tamamlananlar);
      default:
        return sortData(bekleyenUrunler);
    }
  };
  
  const filteredUrunler = getFilteredUrunler();

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('tr-TR') + ' ' + date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  // Sipariş zincirinin toplam orijinal miktarını hesapla
  const getOriginalOrderTotal = (urun) => {
    // delivery_history varsa, ilk teslimattaki beklenen miktarları topla
    if (urun.delivery_history && urun.delivery_history.length > 0) {
      const firstDelivery = urun.delivery_history[0];
      if (firstDelivery.items) {
        return firstDelivery.items.reduce((sum, item) => sum + (item.expected_quantity || item.quantity || 0), 0);
      }
    }
    // Yoksa mevcut items'tan hesapla
    return urun.items?.reduce((sum, item) => sum + (item.expected_quantity || item.quantity || 0), 0) || 0;
  };

  // Sipariş zincirinin toplam alınan miktarını hesapla
  const getTotalReceivedQuantity = (urun) => {
    let total = 0;
    if (urun.delivery_history && urun.delivery_history.length > 0) {
      urun.delivery_history.forEach(delivery => {
        if (delivery.items) {
          delivery.items.forEach(item => {
            total += (item.received_quantity || 0);
          });
        }
      });
    } else {
      // delivery_history yoksa mevcut items'tan hesapla
      urun.items?.forEach(item => {
        total += (item.received_quantity || 0);
      });
    }
    return total;
  };

  // Teslimat sayısını getir
  const getDeliveryCount = (urun) => {
    return urun.delivery_history?.length || 1;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="depot-incoming-loading">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gelecek Ürünler</h1>
            <p className="text-gray-500">Beklenen ürün teslimatları</p>
          </div>
        </div>
        <Card>
          <CardContent className="py-6">
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Bilgilendirme kutusunu kapat ve localStorage'a kaydet
  const hideGelecekUrunlerInfo = () => {
    setShowGelecekUrunlerInfo(false);
    localStorage.setItem('depot_gelecek_urunler_info_hidden', 'true');
  };

  return (
    <div className="space-y-6" data-testid="depot-incoming">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gelecek Ürünler</h1>
          <p className="text-gray-500">Beklenen ürün teslimatları</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="bekliyor" className="gap-2">
            <Clock className="h-4 w-4" />
            BEKLENEN TESLİMATLAR
            {unreadBekleyen > 0 && (
              <Badge className="ml-1 h-5 min-w-5 p-0 flex items-center justify-center text-xs bg-red-500 text-white">
                {unreadBekleyen}
              </Badge>
            )}
            {bekleyenUrunler.length > 0 && unreadBekleyen === 0 && (
              <Badge className="ml-1 h-5 min-w-5 p-0 flex items-center justify-center text-xs bg-blue-100 text-blue-700">
                {bekleyenUrunler.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="eksik" className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            EKSİK KALAN TESLİMATLAR
            {unreadEksik > 0 && (
              <Badge className="ml-1 h-5 min-w-5 p-0 flex items-center justify-center text-xs bg-red-500 text-white">
                {unreadEksik}
              </Badge>
            )}
            {eksikKalanlar.length > 0 && unreadEksik === 0 && (
              <Badge className="ml-1 h-5 min-w-5 p-0 flex items-center justify-center text-xs bg-amber-100 text-amber-700">
                {eksikKalanlar.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tamamlanan" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            TAMAMLANAN TESLİMATLAR
            {unreadTamamlanan > 0 && (
              <Badge className="ml-1 h-5 min-w-5 p-0 flex items-center justify-center text-xs bg-red-500 text-white">
                {unreadTamamlanan}
              </Badge>
            )}
            {tamamlananlar.length > 0 && unreadTamamlanan === 0 && (
              <Badge className="ml-1 h-5 min-w-5 p-0 flex items-center justify-center text-xs bg-emerald-100 text-emerald-700">
                {tamamlananlar.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          <Card>
            <CardHeader className="flex flex-col gap-4">
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5 text-emerald-600" />
                {activeTab === 'bekliyor' ? 'Beklenen Teslimatlar' : activeTab === 'eksik' ? 'Eksik Kalan Teslimatlar' : 'Tamamlanan Teslimatlar'} ({filteredUrunler.length})
              </CardTitle>
              
              {/* Kapanabilir Bilgilendirme Kutusu */}
              {showGelecekUrunlerInfo && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg relative">
                  <button 
                    onClick={hideGelecekUrunlerInfo}
                    className="absolute top-2 right-2 p-1 hover:bg-blue-100 rounded-full transition-colors"
                    aria-label="Bilgilendirmeyi kapat"
                  >
                    <X className="h-4 w-4 text-blue-600" />
                  </button>
                  <div className="flex gap-3 pr-6">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800">
                      Gelecek Ürünler bölümünde BEKLENEN TESLİMATLAR, EKSİK KALAN TESLİMATLAR, TAMAMLANAN TESLİMATLAR gibi bilgilere kolayca ulaşabilir ve ürünlerinizi teslim alabilirsiniz.
                    </p>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {filteredUrunler.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Truck className="h-12 w-12 mb-4 text-gray-300" />
                  <p>Bu kategoride kayıt bulunmuyor</p>
                  {activeTab === 'bekliyor' && (
                    <p className="text-sm mt-1">Satın Alma birimi ürün gönderdiğinde burada görünecektir</p>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('source')}
                      >
                        <div className="flex items-center">
                          Kaynak
                          <SortIcon columnKey="source" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('cari_hesap_name')}
                      >
                        <div className="flex items-center">
                          Tedarikçi
                          <SortIcon columnKey="cari_hesap_name" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('item_count')}
                      >
                        <div className="flex items-center">
                          Ürün Sayısı
                          <SortIcon columnKey="item_count" />
                        </div>
                      </TableHead>
                      {activeTab === 'tamamlanan' && (
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('delivery_count')}
                        >
                          <div className="flex items-center">
                            Teslimat
                            <SortIcon columnKey="delivery_count" />
                          </div>
                        </TableHead>
                      )}
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('order_date')}
                      >
                        <div className="flex items-center">
                          Sipariş Tarihi
                          <SortIcon columnKey="order_date" />
                        </div>
                      </TableHead>
                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('expected_date')}
                      >
                        <div className="flex items-center">
                          Beklenen Teslim
                          <SortIcon columnKey="expected_date" />
                        </div>
                      </TableHead>

                      <TableHead 
                        className="cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center">
                          Durum
                          <SortIcon columnKey="status" />
                        </div>
                      </TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUrunler.map((urun) => (
                      <React.Fragment key={urun.id}>
                        {/* Main Row */}
                        <TableRow 
                          data-testid={`incoming-row-${urun.id}`}
                          className={`cursor-pointer hover:bg-gray-50 ${expandedRows[urun.id] ? 'bg-emerald-50' : ''} ${isUnread(urun) ? 'bg-blue-50/50 border-l-4 border-l-red-500' : ''}`}
                          onClick={() => {
                            toggleRowExpansion(urun.id);
                            if (isUnread(urun)) {
                              markAsRead(urun.id);
                            }
                          }}
                        >
                          <TableCell className="w-10">
                            <div className="flex items-center gap-1">
                              {isUnread(urun) && (
                                <Badge className="h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold animate-pulse">
                                  YENİ
                                </Badge>
                              )}
                              {urun.items && urun.items.length > 1 ? (
                                expandedRows[urun.id] ? (
                                  <ChevronDown className="h-4 w-4 text-emerald-600" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-400" />
                                )
                              ) : (
                                <Package className="h-4 w-4 text-gray-300" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{getSourceBadge(urun.source)}</TableCell>
                          <TableCell className="font-medium">
                            <div className="flex flex-col">
                              {urun.cari_hesap_name || '-'}
                              {urun.parent_order_id && (
                                <Badge className="mt-1 bg-purple-100 text-purple-700 text-xs w-fit gap-1">
                                  <History className="h-3 w-3" />
                                  Takip Siparişi
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="gap-1">
                              <Layers className="h-3 w-3" />
                              {urun.items?.length || 1} Kalem
                            </Badge>
                          </TableCell>
                          {activeTab === 'tamamlanan' && (
                            <TableCell>
                              {(urun.delivery_history?.length > 0 || urun.parent_order_id) ? (
                                <Badge className="gap-1 bg-blue-100 text-blue-700">
                                  <Truck className="h-3 w-3" />
                                  {getDeliveryCount(urun)} teslimat
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="gap-1 text-gray-500">
                                  1 teslimat
                                </Badge>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <div className="text-sm text-gray-700">
                              {formatDateTime(urun.order_date || urun.created_at)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm text-blue-600">
                              <Calendar className="h-4 w-4" />
                              {formatDateTime(urun.expected_date || urun.estimated_arrival_datetime) || '-'}
                            </div>
                          </TableCell>

                          <TableCell>{getStatusBadge(urun.status)}</TableCell>
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDetailModal(urun)}
                                className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                                title="Detay"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {urun.status === 'bekliyor' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openTeslimAlModal(urun)}
                                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  title="Teslim Al"
                                >
                                  <PackageCheck className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded Items Row */}
                        {expandedRows[urun.id] && urun.items && urun.items.length > 0 && (
                          <TableRow className="bg-gray-50">
                            <TableCell colSpan={activeTab === 'tamamlanan' ? 9 : 8} className="p-0">
                              <div className="p-4 border-l-4 border-emerald-400">
                                
                                {/* Tamamlanan ve Eksik Siparişler için Temiz Teslimat Geçmişi Tablosu */}
                                {(activeTab === 'tamamlanan' || activeTab === 'eksik') && urun.delivery_history && urun.delivery_history.length > 0 ? (
                                  <div className="space-y-4">
                                    {/* Ana Teslimat Özet Tablosu */}
                                    <div className="border rounded-lg overflow-hidden">
                                      <Table>
                                        <TableHeader>
                                          <TableRow className="bg-emerald-50">
                                            <TableHead className="text-xs font-semibold w-32">Aşama</TableHead>
                                            <TableHead className="text-xs font-semibold">Tarih / Saat</TableHead>
                                            <TableHead className="text-xs font-semibold">Ürün Adı</TableHead>
                                            <TableHead className="text-xs font-semibold text-center">Sipariş</TableHead>
                                            <TableHead className="text-xs font-semibold text-center">Teslim Alınan</TableHead>
                                            <TableHead className="text-xs font-semibold text-center">Durum</TableHead>
                                            <TableHead className="text-xs font-semibold">Belgeler</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {/* Orijinal Sipariş Satırı */}
                                          {urun.delivery_history[0]?.items?.map((item, iIdx) => (
                                            <TableRow key={`order-${iIdx}`} className="bg-blue-50/50">
                                              {iIdx === 0 && (
                                                <TableCell rowSpan={urun.delivery_history[0].items.length} className="border-r bg-blue-100/50">
                                                  <div className="flex flex-col">
                                                    <Badge className="bg-blue-600 text-white mb-1">SİPARİŞ</Badge>
                                                    <span className="text-xs text-gray-500">{formatDate(urun.order_date || urun.created_at)}</span>
                                                  </div>
                                                </TableCell>
                                              )}
                                              <TableCell className="text-sm text-gray-500">-</TableCell>
                                              <TableCell className="text-sm font-medium">{item.product_name}</TableCell>
                                              <TableCell className="text-sm text-center font-semibold text-blue-600">{item.expected_quantity || item.quantity || 0}</TableCell>
                                              <TableCell className="text-sm text-center text-gray-400">-</TableCell>
                                              <TableCell className="text-center">
                                                <Badge variant="outline" className="text-gray-500">Bekliyor</Badge>
                                              </TableCell>
                                              {iIdx === 0 && (
                                                <TableCell rowSpan={urun.delivery_history[0].items.length} className="border-l">
                                                  {urun.belgeler && urun.belgeler.length > 0 ? (
                                                    <div className="flex flex-wrap gap-1">
                                                      {urun.belgeler.map((belge, bIdx) => (
                                                        <div key={bIdx} className="flex items-center gap-1 bg-blue-100 rounded px-1.5 py-0.5">
                                                          <FileText className="h-3 w-3 text-blue-600" />
                                                          <span className="text-xs text-blue-700 max-w-[80px] truncate" title={belge.name}>{belge.name}</span>
                                                          <a href={belge.data} target="_blank" rel="noopener noreferrer" className="p-0.5 hover:bg-blue-200 rounded" title="Görüntüle">
                                                            <Eye className="h-3 w-3 text-blue-600" />
                                                          </a>
                                                          <button onClick={() => downloadBelge(belge)} className="p-0.5 hover:bg-blue-200 rounded" title="İndir">
                                                            <Download className="h-3 w-3 text-blue-600" />
                                                          </button>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  ) : (
                                                    <span className="text-xs text-gray-400">-</span>
                                                  )}
                                                </TableCell>
                                              )}
                                            </TableRow>
                                          ))}
                                          
                                          {/* Her Teslimat için Satırlar */}
                                          {urun.delivery_history.map((delivery, dIdx) => (
                                            delivery.items?.map((item, iIdx) => (
                                              <TableRow key={`delivery-${dIdx}-${iIdx}`} className={dIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                                {iIdx === 0 && (
                                                  <TableCell rowSpan={delivery.items.length} className={`border-r ${delivery.status === 'teslim_alindi' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                                                    <div className="flex flex-col">
                                                      <Badge className={delivery.status === 'teslim_alindi' ? 'bg-emerald-600 text-white mb-1' : 'bg-amber-500 text-white mb-1'}>
                                                        {dIdx + 1}. TESLİMAT
                                                      </Badge>
                                                      <span className="text-xs text-gray-600 font-medium">
                                                        {formatDate(delivery.received_at)}
                                                      </span>
                                                      <span className="text-xs text-gray-400">
                                                        {delivery.received_at ? new Date(delivery.received_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : '-'}
                                                      </span>
                                                    </div>
                                                  </TableCell>
                                                )}
                                                <TableCell className="text-sm">
                                                  {formatDateTime(delivery.received_at)}
                                                </TableCell>
                                                <TableCell className="text-sm font-medium">{item.product_name}</TableCell>
                                                <TableCell className="text-sm text-center text-blue-600">{item.expected_quantity || item.quantity || 0}</TableCell>
                                                <TableCell className="text-sm text-center font-semibold text-emerald-600">{item.received_quantity || 0}</TableCell>
                                                {iIdx === 0 && (
                                                  <TableCell rowSpan={delivery.items.length} className="text-center border-l">
                                                    <Badge className={delivery.status === 'teslim_alindi' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                                      {delivery.status === 'teslim_alindi' ? '✓ Tamamlandı' : '⚠ Eksik Teslim'}
                                                    </Badge>
                                                  </TableCell>
                                                )}
                                                {iIdx === 0 && (
                                                  <TableCell rowSpan={delivery.items.length} className="border-l">
                                                    {delivery.belgeler && delivery.belgeler.length > 0 ? (
                                                      <div className="flex flex-wrap gap-1">
                                                        {delivery.belgeler.map((belge, bIdx) => (
                                                          <div key={bIdx} className="flex items-center gap-1 bg-emerald-100 rounded px-1.5 py-0.5">
                                                            <FileText className="h-3 w-3 text-emerald-600" />
                                                            <span className="text-xs text-emerald-700 max-w-[80px] truncate" title={belge.name}>{belge.name}</span>
                                                            <a href={belge.data} target="_blank" rel="noopener noreferrer" className="p-0.5 hover:bg-emerald-200 rounded" title="Görüntüle">
                                                              <Eye className="h-3 w-3 text-emerald-600" />
                                                            </a>
                                                            <button onClick={() => downloadBelge(belge)} className="p-0.5 hover:bg-emerald-200 rounded" title="İndir">
                                                              <Download className="h-3 w-3 text-emerald-600" />
                                                            </button>
                                                          </div>
                                                        ))}
                                                      </div>
                                                    ) : (
                                                      <span className="text-xs text-gray-400">-</span>
                                                    )}
                                                  </TableCell>
                                                )}
                                              </TableRow>
                                            ))
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>
                                    
                                    {/* Sipariş Özeti */}
                                    <div className={`p-3 rounded-lg border ${activeTab === 'tamamlanan' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                                      <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="flex items-center gap-2">
                                          {activeTab === 'tamamlanan' ? (
                                            <>
                                              <PackageCheck className="h-5 w-5 text-emerald-600" />
                                              <span className="font-semibold text-emerald-800">Sipariş Tamamlandı</span>
                                            </>
                                          ) : (
                                            <>
                                              <AlertTriangle className="h-5 w-5 text-amber-600" />
                                              <span className="font-semibold text-amber-800">Eksik Teslimat - Devam Ediyor</span>
                                            </>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-6 text-sm">
                                          <span className="text-gray-600">
                                            Toplam Sipariş: <span className="font-bold text-blue-600">{getOriginalOrderTotal(urun)}</span>
                                          </span>
                                          <span className="text-gray-600">
                                            Toplam Teslim: <span className={`font-bold ${activeTab === 'tamamlanan' ? 'text-emerald-600' : 'text-amber-600'}`}>{getTotalReceivedQuantity(urun)}</span>
                                          </span>
                                          {activeTab === 'eksik' && (
                                            <span className="text-gray-600">
                                              Kalan: <span className="font-bold text-red-600">{getOriginalOrderTotal(urun) - getTotalReceivedQuantity(urun)}</span>
                                            </span>
                                          )}
                                          <span className="text-gray-600">
                                            Teslimat Sayısı: <span className="font-bold text-purple-600">{getDeliveryCount(urun)}</span>
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Belgeler - Her Teslimat için Ayrı */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* Sipariş Belgeleri */}
                                      {urun.belgeler && urun.belgeler.length > 0 && (
                                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                          <div className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-2">
                                            <FileText className="h-4 w-4" />
                                            SİPARİŞ BELGELERİ
                                          </div>
                                          <div className="flex flex-wrap gap-2">
                                            {urun.belgeler.map((belge, bIdx) => (
                                              <div key={bIdx} className="flex items-center gap-1 bg-white border rounded px-2 py-1">
                                                <FileText className="h-3 w-3 text-blue-500" />
                                                <span className="text-sm">{belge.name}</span>
                                                <a 
                                                  href={belge.data}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="p-1 hover:bg-blue-100 rounded transition-colors"
                                                  title="Görüntüle"
                                                >
                                                  <Eye className="h-3 w-3 text-blue-600" />
                                                </a>
                                                <button
                                                  onClick={() => downloadBelge(belge)}
                                                  className="p-1 hover:bg-blue-100 rounded transition-colors"
                                                  title="İndir"
                                                >
                                                  <Download className="h-3 w-3 text-blue-600" />
                                                </button>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Teslimat Belgeleri */}
                                      {urun.delivery_history.some(d => d.belgeler && d.belgeler.length > 0) && (
                                        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                                          <div className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                                            <FileText className="h-4 w-4" />
                                            TESLİMAT BELGELERİ
                                          </div>
                                          <div className="space-y-2">
                                            {urun.delivery_history.map((delivery, dIdx) => (
                                              delivery.belgeler && delivery.belgeler.length > 0 && (
                                                <div key={dIdx} className="flex items-center gap-2 flex-wrap">
                                                  <Badge variant="outline" className="text-xs">{dIdx + 1}. Teslimat</Badge>
                                                  <div className="flex flex-wrap gap-1">
                                                    {delivery.belgeler.map((belge, bIdx) => (
                                                      <div key={bIdx} className="flex items-center gap-1 bg-white border rounded px-2 py-1">
                                                        <FileText className="h-3 w-3 text-emerald-500" />
                                                        <span className="text-xs">{belge.name}</span>
                                                        <a 
                                                          href={belge.data}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="p-1 hover:bg-emerald-100 rounded transition-colors"
                                                          title="Görüntüle"
                                                        >
                                                          <Eye className="h-3 w-3 text-emerald-600" />
                                                        </a>
                                                        <button
                                                          onClick={() => downloadBelge(belge)}
                                                          className="p-1 hover:bg-emerald-100 rounded transition-colors"
                                                          title="İndir"
                                                        >
                                                          <Download className="h-3 w-3 text-emerald-600" />
                                                        </button>
                                                      </div>
                                                    ))}
                                                  </div>
                                                </div>
                                              )
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  /* Normal siparişler için mevcut görünüm */
                                  <>
                                    {/* Takip Siparişi Banner */}
                                    {urun.parent_order_id && (
                                      <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
                                        <div className="flex items-center gap-2">
                                          <History className="h-5 w-5 text-purple-600" />
                                          <span className="font-semibold text-purple-800">Takip Siparişi</span>
                                          <span className="text-sm text-purple-600">- Önceki eksik teslimat için oluşturuldu</span>
                                        </div>
                                      </div>
                                    )}
                                    
                                    <div className="text-xs font-semibold text-gray-500 mb-2">ÜRÜN KALEMLERİ ({urun.items.length})</div>
                                    <Table>
                                      <TableHeader>
                                        <TableRow className="bg-gray-100">
                                          <TableHead className="text-xs">Ürün Adı</TableHead>
                                          <TableHead className="text-xs">Beklenen</TableHead>
                                          <TableHead className="text-xs">İrsaliye</TableHead>
                                          <TableHead className="text-xs">Teslim Alınan</TableHead>
                                          <TableHead className="text-xs">Birim</TableHead>
                                          {activeTab === 'eksik' && (
                                            <TableHead className="text-xs">Teslim Tarihi</TableHead>
                                          )}
                                          <TableHead className="text-xs">Açıklama</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {urun.items.map((item, idx) => {
                                          const hasShortage = (item.expected_quantity || item.quantity) > (item.received_quantity || 0);
                                          return (
                                            <TableRow key={item.id || idx} className={`hover:bg-gray-100 ${hasShortage ? 'bg-amber-50' : ''}`}>
                                              <TableCell className="text-sm font-medium">
                                                <div className="flex items-center gap-2">
                                                  {item.product_name || '-'}
                                                  {hasShortage && (
                                                    <Badge className="bg-amber-100 text-amber-700 text-xs">
                                                      <AlertTriangle className="h-3 w-3 mr-1" />
                                                      Eksik
                                                    </Badge>
                                                  )}
                                                </div>
                                              </TableCell>
                                              <TableCell className="text-sm text-blue-600 font-medium">{item.expected_quantity || item.quantity || 0}</TableCell>
                                              <TableCell className="text-sm text-purple-600">{item.irsaliye_quantity || '-'}</TableCell>
                                              <TableCell className={`text-sm font-medium ${hasShortage ? 'text-amber-600' : 'text-emerald-600'}`}>
                                                {item.received_quantity || 0}
                                              </TableCell>
                                              <TableCell className="text-sm">{item.unit || 'adet'}</TableCell>
                                              {activeTab === 'eksik' && (
                                                <TableCell className="text-sm">
                                                  <div className="flex items-center gap-1 text-emerald-600">
                                                    <Calendar className="h-3 w-3" />
                                                    {formatDate(item.received_date || urun.received_at)}
                                                  </div>
                                                </TableCell>
                                              )}
                                              <TableCell className="text-sm">
                                                {item.aciklama ? (
                                                  <span className="text-gray-600 truncate max-w-[200px]" title={item.aciklama}>
                                                    {item.aciklama}
                                                  </span>
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                    
                                    {/* Belgeler */}
                                    {urun.belgeler && urun.belgeler.length > 0 && (
                                      <div className="mt-4 pt-4 border-t">
                                        <div className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-2">
                                          <FileText className="h-4 w-4" />
                                          BELGELER ({urun.belgeler.length})
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                          {urun.belgeler.map((belge, bIdx) => (
                                            <div key={bIdx} className="flex items-center gap-2 bg-white border rounded px-3 py-2">
                                              <FileText className="h-4 w-4 text-blue-500" />
                                              <span className="text-sm">{belge.name}</span>
                                              <a 
                                                href={belge.data}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-1 hover:bg-blue-100 rounded transition-colors"
                                                title="Görüntüle"
                                              >
                                                <Eye className="h-4 w-4 text-blue-600" />
                                              </a>
                                              <button
                                                onClick={() => downloadBelge(belge)}
                                                className="p-1 hover:bg-blue-100 rounded transition-colors"
                                                title="İndir"
                                              >
                                                <Download className="h-4 w-4 text-blue-600" />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Teslimat Geçmişi Butonu - sadece eksik için */}
                                    {activeTab === 'eksik' && (urun.delivery_history?.length > 0 || urun.parent_order_id || urun.root_order_id) && (
                                      <div className="mt-4 pt-4 border-t">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => fetchDeliveryHistory(urun.id)}
                                          className="flex items-center gap-2"
                                        >
                                          <History className="h-4 w-4" />
                                          Teslimat Geçmişini Görüntüle
                                        </Button>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editMode ? 'Kayıt Düzenle' : 'Yeni Gelecek Ürün'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tedarikçi (Cari Hesap)</Label>
              <select
                value={form.cari_hesap_id}
                onChange={handleCariSelect}
                className="w-full rounded-md border border-gray-300 p-2"
              >
                <option value="">Seçiniz...</option>
                {cariHesaplar.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Ürün Adı *</Label>
              <Input
                value={form.product_name}
                onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                placeholder="Ürün adı"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Miktar</Label>
                <Input
                  type="number"
                  value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <Label>Birim</Label>
                <select
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  className="w-full rounded-md border border-gray-300 p-2"
                >
                  {birimOptions.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label>Beklenen Tarih</Label>
              <Input
                type="date"
                value={form.expected_date}
                onChange={(e) => setForm({ ...form, expected_date: e.target.value })}
              />
            </div>
            {editMode && (
              <div>
                <Label>Durum</Label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full rounded-md border border-gray-300 p-2"
                >
                  <option value="bekliyor">Bekliyor</option>
                  <option value="teslim_alindi">Teslim Alındı</option>
                  <option value="iptal">İptal</option>
                </select>
              </div>
            )}
            <div>
              <Label>Notlar</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Ek notlar..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>İptal</Button>
            <Button onClick={handleSubmit} className="bg-emerald-600 hover:bg-emerald-700">
              {editMode ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teslim Al Modal */}
      <Dialog open={teslimAlModal} onOpenChange={setTeslimAlModal}>
        <DialogContent className="max-w-3xl" style={{maxHeight: '90vh', display: 'flex', flexDirection: 'column'}}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="h-5 w-5 text-emerald-600" />
              Ürün Teslim Al
            </DialogTitle>
          </DialogHeader>
          
          {selectedUrun && (
            <div className="overflow-y-auto flex-1 pr-2" style={{maxHeight: '60vh', overflowY: 'scroll'}}>
              <div className="space-y-6">
                {/* Kaynak Bilgisi */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Kaynak:</span>
                      <span className="ml-2">{getSourceBadge(selectedUrun.source)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Tedarikçi:</span>
                      <span className="ml-2 font-medium">{selectedUrun.cari_hesap_name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Tarih:</span>
                      <span className="ml-2">{formatDate(selectedUrun.created_at)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Gönderen:</span>
                      <span className="ml-2">{selectedUrun.created_by_name || '-'}</span>
                    </div>
                  </div>
                </div>

                {/* Ürün Listesi */}
                <div>
                  <Label className="text-base font-semibold mb-3 block">Ürünler ({teslimItems.length} kalem) - Aşağı kaydırarak tüm ürünleri görebilirsiniz</Label>
                  <div className="space-y-4">
                    {teslimItems.map((item, index) => {
                      const shortage = item.expected_quantity - item.received_quantity;
                      const hasShortage = shortage > 0;
                      
                      return (
                        <div 
                          key={index} 
                          className={`p-4 rounded-lg border-2 ${hasShortage ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white'}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Package className="h-5 w-5 text-emerald-600" />
                              <span className="font-medium">{index + 1}. {item.product_name}</span>
                            </div>
                            {hasShortage && (
                              <Badge className="bg-amber-100 text-amber-700">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {shortage} {item.unit} eksik
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-4 gap-3 mb-3">
                            <div>
                              <Label className="text-xs text-gray-500">Satın Alma Miktarı</Label>
                              <div className="p-2 bg-blue-50 rounded text-center border border-blue-200">
                                <span className="text-lg font-bold text-blue-600">
                                  {item.expected_quantity}
                                </span>
                                <span className="text-xs text-blue-500 ml-1">{item.unit}</span>
                              </div>
                              <p className="text-xs text-gray-400 mt-1 text-center">Salt okunur</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">İrsaliyede Yazan</Label>
                              <Input
                                type="number"
                                min="0"
                                value={item.irsaliye_quantity}
                                onChange={(e) => handleIrsaliyeQuantityChange(index, e.target.value)}
                                className="text-center"
                                placeholder="Miktar"
                                data-testid={`irsaliye-quantity-${index}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Teslim Alınan *</Label>
                              <Input
                                type="number"
                                min="0"
                                value={item.received_quantity}
                                onChange={(e) => handleTeslimQuantityChange(index, e.target.value)}
                                className={`font-medium text-center ${hasShortage ? 'border-amber-300 bg-amber-50' : ''}`}
                                data-testid={`received-quantity-${index}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Birim</Label>
                              <div className="p-2 bg-gray-100 rounded text-center h-10 flex items-center justify-center">
                                {item.unit}
                              </div>
                            </div>
                          </div>
                          
                          {/* Ürün Açıklaması */}
                          <div className="mt-3">
                            <Label className="text-xs text-gray-500 flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              Açıklama (Eksik/Yanlış ürünler için)
                            </Label>
                            <Textarea
                              value={item.aciklama}
                              onChange={(e) => handleAciklamaChange(index, e.target.value)}
                              placeholder="Bu ürün ile ilgili açıklama yazın (eksik, hasarlı, yanlış ürün vb.)"
                              rows={2}
                              className="text-sm mt-1"
                              data-testid={`item-aciklama-${index}`}
                            />
                          </div>
                          
                          {hasShortage && (
                            <div className="mt-3 p-2 bg-amber-100 rounded text-sm text-amber-800">
                              <AlertTriangle className="h-4 w-4 inline mr-1" />
                              Eksik miktar otomatik olarak "Hatalı/Eksik Ürünler" listesine kaydedilecektir.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Belge Yükleme */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                    <FileCheck className="h-5 w-5 text-blue-600" />
                    Belgeler (İrsaliye / Fatura)
                  </Label>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id="belge-upload"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={handleBelgeUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('belge-upload').click()}
                        className="gap-2"
                      >
                        <Upload className="h-4 w-4" />
                        Belge Ekle
                      </Button>
                      <span className="text-xs text-gray-500">PDF, JPG, PNG, DOC formatları desteklenir</span>
                    </div>
                    
                    {teslimBelgeler.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {teslimBelgeler.map((belge, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-white border rounded px-3 py-2">
                            <FileText className="h-4 w-4 text-blue-500" />
                            <span className="text-sm">{belge.name}</span>
                            <button
                              type="button"
                              onClick={() => removeBelge(idx)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Teslim Notu */}
                <div>
                  <Label>Teslim Notu</Label>
                  <Textarea
                    value={teslimNotu}
                    onChange={(e) => setTeslimNotu(e.target.value)}
                    placeholder="Teslim alma ile ilgili notlarınız..."
                    rows={3}
                  />
                </div>

                {/* Belgeler */}
                {selectedUrun.documents && selectedUrun.documents.length > 0 && (
                  <div>
                    <Label className="text-sm text-gray-500 mb-2 block">Ekli Belgeler</Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedUrun.documents.map((doc, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-100 rounded px-3 py-2">
                          <FileText className="h-4 w-4 text-blue-500" />
                          <span className="text-sm">{doc.name}</span>
                          {doc.data && (
                            <a href={doc.data} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                              Görüntüle
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4 border-t pt-4">
            {teslimBelgeler.length === 0 && (
              <div className="flex-1 flex items-center text-amber-600 text-sm">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Teslim almak için en az bir belge yüklemeniz zorunludur
              </div>
            )}
            <Button variant="outline" onClick={() => setTeslimAlModal(false)}>İptal</Button>
            <Button 
              onClick={handleTeslimAl} 
              disabled={teslimSubmitting || teslimBelgeler.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              data-testid="confirm-teslim-al"
            >
              <PackageCheck className="h-4 w-4" />
              {teslimSubmitting ? 'İşleniyor...' : 'Teslim Al'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModal} onOpenChange={setDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Kayıt Detayı</DialogTitle>
          </DialogHeader>
          {detailData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Kaynak</Label>
                  <p>{getSourceBadge(detailData.source)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <p>{getStatusBadge(detailData.status)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Tedarikçi</Label>
                  <p className="font-medium">{detailData.cari_hesap_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturma Tarihi</Label>
                  <p className="font-medium">{formatDate(detailData.created_at)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturan</Label>
                  <p className="font-medium">{detailData.created_by_name || '-'}</p>
                </div>
                {detailData.received_at && (
                  <div>
                    <Label className="text-gray-500">Teslim Tarihi</Label>
                    <p className="font-medium">{formatDate(detailData.received_at)}</p>
                  </div>
                )}
                {detailData.received_by_name && (
                  <div>
                    <Label className="text-gray-500">Teslim Alan</Label>
                    <p className="font-medium">{detailData.received_by_name}</p>
                  </div>
                )}
              </div>

              {/* Ürünler */}
              <div>
                <Label className="text-gray-500 mb-2 block">Ürünler</Label>
                <div className="space-y-2">
                  {detailData.items && detailData.items.length > 0 ? (
                    detailData.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-emerald-500" />
                          <span className="font-medium">{item.product_name}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-medium text-emerald-600">{item.quantity} {item.unit}</span>
                          {item.received_quantity !== undefined && item.received_quantity !== item.quantity && (
                            <span className="ml-2 text-sm text-amber-600">
                              (Teslim: {item.received_quantity})
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-emerald-500" />
                        <span className="font-medium">{detailData.product_name}</span>
                      </div>
                      <span className="font-medium text-emerald-600">{detailData.quantity} {detailData.unit}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Açıklama */}
              {(detailData.description || detailData.notes) && (
                <div>
                  <Label className="text-gray-500">Açıklama</Label>
                  <p className="font-medium">{detailData.description || detailData.notes}</p>
                </div>
              )}

              {/* Teslim Notu */}
              {detailData.teslim_notu && (
                <div>
                  <Label className="text-gray-500">Teslim Notu</Label>
                  <p className="font-medium">{detailData.teslim_notu}</p>
                </div>
              )}

              {/* Belgeler */}
              {detailData.documents && detailData.documents.length > 0 && (
                <div>
                  <Label className="text-gray-500 mb-2 block">Belgeler ({detailData.documents.length})</Label>
                  <div className="flex flex-wrap gap-2">
                    {detailData.documents.map((doc, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                        <FileText className="h-4 w-4 text-blue-500" />
                        <span className="text-sm">{doc.name}</span>
                        {doc.data && (
                          <a href={doc.data} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                            Aç
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teslimat Geçmişi Modal */}
      <Dialog open={historyModal} onOpenChange={setHistoryModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600" />
              Teslimat Geçmişi
            </DialogTitle>
          </DialogHeader>
          
          {historyLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : historyData ? (
            <div className="space-y-6">
              {/* Özet Bilgiler */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm text-blue-600 font-medium">Toplam Teslimat</div>
                  <div className="text-2xl font-bold text-blue-700">{historyData.summary?.total_deliveries || 0}</div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4">
                  <div className="text-sm text-emerald-600 font-medium">İlişkili Sipariş</div>
                  <div className="text-2xl font-bold text-emerald-700">{historyData.orders_in_chain || 1}</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="text-sm text-purple-600 font-medium">İlk Teslimat</div>
                  <div className="text-sm font-bold text-purple-700">
                    {historyData.summary?.first_delivery ? new Date(historyData.summary.first_delivery).toLocaleDateString('tr-TR') : '-'}
                  </div>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="text-sm text-amber-600 font-medium">Son Teslimat</div>
                  <div className="text-sm font-bold text-amber-700">
                    {historyData.summary?.last_delivery ? new Date(historyData.summary.last_delivery).toLocaleDateString('tr-TR') : '-'}
                  </div>
                </div>
              </div>

              {/* Toplam Beklenen vs Teslim Alınan */}
              {historyData.total_expected && historyData.total_expected.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Toplam Özet
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ürün</TableHead>
                        <TableHead className="text-center">Beklenen</TableHead>
                        <TableHead className="text-center">Teslim Alınan</TableHead>
                        <TableHead className="text-center">Kalan</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyData.total_expected.map((expected, idx) => {
                        const received = historyData.total_received?.find(r => 
                          r.product_name === expected.product_name
                        ) || { quantity: 0 };
                        const remaining = expected.quantity - received.quantity;
                        return (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{expected.product_name}</TableCell>
                            <TableCell className="text-center text-blue-600">{expected.quantity} {expected.unit}</TableCell>
                            <TableCell className="text-center text-emerald-600">{received.quantity} {expected.unit}</TableCell>
                            <TableCell className={`text-center font-semibold ${remaining > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              {remaining > 0 ? remaining : '✓'} {remaining > 0 ? expected.unit : ''}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Teslimat Geçmişi Timeline */}
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Teslimat Zaman Çizelgesi
                </h4>
                
                {historyData.delivery_history && historyData.delivery_history.length > 0 ? (
                  <div className="space-y-4">
                    {historyData.delivery_history.map((delivery, dIdx) => (
                      <div key={delivery.delivery_id || dIdx} className="relative pl-6 pb-4 border-l-2 border-blue-200 last:border-l-0">
                        <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow"></div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <div className="font-semibold text-gray-800">
                                Teslimat #{dIdx + 1}
                              </div>
                              <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(delivery.delivery_date).toLocaleString('tr-TR')}
                              </div>
                            </div>
                            <Badge className={delivery.status === 'teslim_alindi' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {delivery.status === 'teslim_alindi' ? 'Tam Teslim' : 'Eksik Teslim'}
                            </Badge>
                          </div>
                          
                          {delivery.received_by_name && (
                            <div className="text-sm text-gray-600 mb-2">
                              Teslim Alan: <span className="font-medium">{delivery.received_by_name}</span>
                            </div>
                          )}
                          
                          {/* Teslimat detayları */}
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-white">
                                <TableHead className="text-xs">Ürün</TableHead>
                                <TableHead className="text-xs text-center">Beklenen</TableHead>
                                <TableHead className="text-xs text-center">Teslim Alınan</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {delivery.items?.map((item, iIdx) => (
                                <TableRow key={iIdx}>
                                  <TableCell className="text-sm">{item.product_name}</TableCell>
                                  <TableCell className="text-sm text-center text-blue-600">{item.expected_quantity} {item.unit}</TableCell>
                                  <TableCell className={`text-sm text-center font-medium ${item.received_quantity < item.expected_quantity ? 'text-amber-600' : 'text-emerald-600'}`}>
                                    {item.received_quantity} {item.unit}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          
                          {delivery.teslim_notu && (
                            <div className="mt-3 text-sm bg-white p-2 rounded border">
                              <span className="text-gray-500">Not: </span>
                              {delivery.teslim_notu}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-8">
                    <History className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>Henüz teslimat geçmişi bulunmuyor</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              Veri yüklenemedi
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepotIncoming;
